import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { TariffCard } from '../../components/features/TariffCard';
import { listTariffs, initPayment } from '../../api/tariffs';
import { useCurrentTariff } from '../../hooks/useCurrentTariff';
import { useAuthStore } from '../../store/auth.store';
import { extractErrorMessage } from '../../api/client';
import type { Tariff } from '../../api/types';

export function TariffsPage() {
  const userGUID = useAuthStore((s) => s.user?.guid);
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busyId, setBusyId] = useState<number | null>(null);

  const tariffsQuery = useQuery({
    queryKey: ['tariffs'],
    queryFn: listTariffs,
    staleTime: 5 * 60_000,
  });

  const current = useCurrentTariff();

  const paymentMutation = useMutation({
    mutationFn: async (tariff: Tariff) => {
      if (!userGUID) throw new Error('No user');
      setBusyId(tariff.id);
      return initPayment(userGUID, tariff.id);
    },
    onSuccess: (res) => {
      window.location.href = res.paymentUrl;
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Не удалось инициировать оплату'));
    },
    onSettled: () => setBusyId(null),
  });

  useEffect(() => {
    if (params.get('paid') === '1') {
      toast.success('Оплата прошла. Тариф активирован.');
      // Re-fetch пары раз после возврата с YooKassa — webhook может прийти
      // с задержкой.
      queryClient.invalidateQueries({ queryKey: ['tariff', userGUID] });
      const t = setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ['tariff', userGUID] }),
        3_000,
      );
      params.delete('paid');
      setParams(params, { replace: true });
      return () => clearTimeout(t);
    }
    return undefined;
  }, [params, setParams, queryClient, userGUID]);

  // Сервер возвращает последний купленный тариф строкой имени и флаг активности.
  // Матчим по имени — id для текущего тарифа в ответе /user/:guid/tariff нет.
  const currentName = current.data?.tariff;
  const currentIsActive = Boolean(current.data?.tariffActive);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold">Тарифы</h1>
        <p className="mt-1 text-sm text-text-muted">
          Оплачивайте пакеты страниц — чем больше объём, тем выгоднее.
        </p>
      </div>

      {tariffsQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full rounded-[18px]" />
          ))}
        </div>
      ) : tariffsQuery.data && tariffsQuery.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tariffsQuery.data.map((t, i) => {
            const isCurrent = currentName === t.name;
            return (
              <div
                key={t.id}
                className="animate-enter-spring"
                style={{ animationDelay: `${Math.min(i, 4) * 60}ms` }}
              >
                <TariffCard
                  tariff={t}
                  isCurrent={isCurrent}
                  isActive={isCurrent && currentIsActive}
                  loading={busyId === t.id}
                  onBuy={(tariff) => paymentMutation.mutate(tariff)}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title="Тарифы недоступны"
          description="Попробуйте обновить страницу позже."
        />
      )}
    </div>
  );
}
